import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import {
  useMonitoringLocation,
  useUpdateMonitoringLocation,
} from "@/hooks/use-monitoring-locations";
import { useToast } from "@/hooks/use-toast";
import { LocationForm } from "@/components/monitoring-locations/location-form";
import { useFacility } from "@/hooks/use-facilities";
import { PageHeader, Breadcrumbs, PageSkeleton } from "@/components/ui";

interface SearchParams {
  facilityId?: string;
}

export const Route = createFileRoute("/_authenticated/monitoring-locations/$locationId/edit")({
  component: UnifiedEditLocationPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
  }),
});

function UnifiedEditLocationPage() {
  const { t } = useTranslation(["monitoringLocations", "common", "nav", "facilities"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const { locationId } = Route.useParams();
  const { facilityId } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);
  const { data: facility } = useFacility(orgId, facilityId ?? "");
  const facilityName = facility?.name ?? facilityId ?? "";

  const {
    data: location,
    isLoading,
    isError,
  } = useMonitoringLocation(orgId, facilityId ?? "", locationId);
  const mutation = useUpdateMonitoringLocation(orgId, facilityId ?? "", locationId);

  const breadcrumbs = facilityId
    ? [
        { label: t("facilities", { ns: "nav" }), to: "/facilities" },
        { label: facilityName, to: `/facilities/${facilityId}` },
        {
          label: t("title"),
          to: `/monitoring-locations?facilityId=${facilityId}`,
        },
        {
          label: location?.name ?? locationId,
          to: `/monitoring-locations/${locationId}?facilityId=${facilityId}`,
        },
        { label: t("edit") },
      ]
    : [
        {
          label: t("title"),
          to: "/monitoring-locations",
        },
        {
          label: location?.name ?? locationId,
          to: `/monitoring-locations/${locationId}`,
        },
        { label: t("edit") },
      ];

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !location) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader title={t("loadFailed", { ns: "facilities" })} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader title={t("editLocation")} />

      <div className="mt-6">
        <LocationForm
          mode="edit"
          initialValues={{
            name: location.name,
            location_code: location.location_code,
            location_type: location.location_type,
            latitude: location.latitude,
            longitude: location.longitude,
            description: location.description,
            receiving_water_body: location.receiving_water_body,
            receiving_water_segment_id: location.receiving_water_segment_id,
          }}
          isSubmitting={mutation.isPending}
          onSubmit={(values) => {
            mutation.mutate(values, {
              onSuccess: () => {
                toasts.success("monitoringLocations:updated");
                void navigate({
                  to: "/monitoring-locations/$locationId",
                  params: { locationId },
                  search: facilityId ? { facilityId } : {},
                });
              },
              onError: () => toasts.error("monitoringLocations:updateFailed"),
            });
          }}
        />
      </div>
    </div>
  );
}
